/**
 * @module InternetBankingActions
 * @requires InternetBankingConstants
 * @requires AppDispatcher
 * @requires appConstants
 */

import InternetBankingConstants from '../constants/InternetBankingConstants';

import AppDispatcher from '../dispatcher/AppDispatcher';
import AppConstants from '../constants/AppConstants';
import AppActions from './AppActions';
import SessionActionCreator from './SessionActionCreator';
import SiteHeaderUtils from '../utils/SiteHeaderUtils';

/**
 * @class InternetBankingActions
 */
const InternetBankingActions = {
  /**
   * Setts the product code
   *
   * @param {String} productCode Product code to set
   *
   */
  setProductCode(productCode) {
    InternetBankingActions.raise(InternetBankingConstants.SET_PRODUCT_CODE, {
      productCode,
    });
  },

  raise(actionType, data) {
    AppDispatcher.handleViewAction({
      actionType,
      data,
    });
  },

  /**
   * User has been given an offer and we need to retrieve the details.
   *
   * @param {String} caseId 			The caseId for the application.
   */
  requestProductOffer(caseId) {
    InternetBankingActions.raise(
      InternetBankingConstants.REQUEST_PRODUCT_OFFER,
      { caseId }
    );
  },

  /**
   * Accept/decline a product offer.
   * When multiple products are offered in future, this should
   * be extended to take arrays of yes / no.
   *
   * @param  {String} productId 			Product to accept.
   * @param  {String} offerProductCode 	code of chosen product offer
   */
  respondToProductOffer(offerId, isDecline, offerProductCode) {
    InternetBankingActions.raise(
      InternetBankingConstants.RESPOND_TO_PRODUCT_OFFER,
      { offerId, isDecline, offerProductCode }
    );
  },

  /**
   * User has changed a value in the form.
   *
   * @param  {String} key   		Which value was changed?
   * @param  {Any} value 			What is the new value?
   * @param  {String} arrayName 	Optional value, used if the value should be stored in an array
   * @param  {String}  arrayIndex	Optional value, specifies the index within the array to update
   */
  updateFormValue(key, value, arrayName, arrayIndex) {
    InternetBankingActions.raise(InternetBankingConstants.UPDATE_FORM_VALUE, {
      key,
      value,
      arrayName,
      arrayIndex,
    });
  },

  /**
   * User has changed a value in the form.
   *
   * @param  {Array} data 	List of objects, each with a "key" and "value" pair.
   */
  updateFormValues(data) {
    InternetBankingActions.raise(
      InternetBankingConstants.UPDATE_FORM_VALUES,
      data
    );
  },

  /**
   * Responsible for raising the action to clear supplied addresses
   *
   * @param  {Array} addressIndexes 	Array of address indexes to be removed
   *
   */
  clearAddress(addressIndexes) {
    InternetBankingActions.raise(InternetBankingConstants.CLEAR_ADDRESS, {
      addressIndexes,
    });
  },

  /**
   * Responsible for raising the action to reset supplied address
   *
   * @param  {Array} addressIndex Index of the address to reset
   *
   */
  resetAddress(addressIndex) {
    InternetBankingActions.raise(InternetBankingConstants.RESET_ADDRESS, {
      addressIndex,
    });
  },

  /**
   * User has entered a new value Is it valid?
   *
   * @param  {String}  group   	Inputs are grouped by form.
   * @param  {String}  key     	Name of the input.
   * @param  {Boolean} isValid 	True if the value passes validation.
   */
  updateValidation(group, key, isValid) {
    InternetBankingActions.raise(InternetBankingConstants.UPDATE_VALIDATION, {
      group,
      key,
      isValid,
    });
  },

  /**
   * User removed a question. Is the form valid?
   *
   * @param  {String}  group   	Inputs are grouped by form.
   * @param  {String}  key     	Name of the input.
   */
  removeValidation(group, key) {
    InternetBankingActions.raise(InternetBankingConstants.REMOVE_VALIDATION, {
      group,
      key,
    });
  },

  /**
   * The given field can now be visibly validated (i.e. show error messages)
   *
   * @param  {String}  group   	Inputs are grouped by form.
   * @param  {String}  key     	Name of the input.
   */
  enableValidation(group, key) {
    InternetBankingActions.raise(InternetBankingConstants.ENABLE_VALIDATION, {
      group,
      key,
    });
  },

  /**
   * Send all data captured in the "data capture" phase of account opening.
   *
   * @param {Boolean} formComplete		Has the user finished the form now?
   */
  sendFormData(formComplete) {
    InternetBankingActions.raise(InternetBankingConstants.SEND_FORM_DATA, {
      formComplete: !!formComplete,
    });
  },

  /**
   * Fetch the user's form data, including account numbers if the account
   * has now been setup.
   *
   * @param {String} caseId 		Id of the case to fetch.
   * @param {String} productType 		Type of product we are fetching
   */
  getCase(caseId, productType) {
    InternetBankingActions.raise(InternetBankingConstants.GET_CASE, {
      caseId,
      productType,
    });
  },

  /**
   * What screen should the user be on?
   *
   * @param {String} caseId 		User case ID.
   */
  getNextTask(caseId) {
    InternetBankingActions.raise(AppConstants.GET_NEXT_TASK, { caseId });
  },

  /**
   * Take the user to the URL represented by the given task ID.
   *
   * This should never be called with CSAP tasks. They should be fetched
   * only from the API (via InternetBankingActions.getNextTask) so that
   * we are never out of sync with CSAP.
   */
  navigateToWebTask(taskId) {
    SiteHeaderUtils.updateCurrentMenu(taskId);
    if (taskId === 'WEB-AUTHENTICATION') {
      AppActions.logoutUser();
      InternetBankingActions.resetUsername();
      SessionActionCreator.requestAccessTokenReset();
    }
    InternetBankingActions.raise(AppConstants.NAVIGATE_TO_WEB_TASK, { taskId });
  },

  /**
   * Clear stored task IDs.
   */
  clearTasks() {
    InternetBankingActions.raise(AppConstants.CLEAR_TASKS);
  },

  /**
   * Clear stored web task ID.
   */
  clearWebTask() {
    InternetBankingActions.raise(AppConstants.CLEAR_WEB_TASK);
  },

  /**
   * Track when the user is/isn't editing a field in the form.
   *
   * @param {Boolean} isEditing Are they currently editing a field?
   */
  setUserIsEditingField(isEditing) {
    InternetBankingActions.raise(
      InternetBankingConstants.SET_USER_IS_EDITING_FIELD,
      { isEditing }
    );
  },

  /**
   * Submit a CAS application.
   */
  submitSwitchingApplication() {
    InternetBankingActions.raise(
      InternetBankingConstants.SUBMIT_SWITCHING_APPLICATION
    );
  },

  /**
   * Submit the registration page.
   *
   * @param {String} publicKey 		For encryption.
   * @param {String} keyDatetime 		Timestamp when the key was generated.
   * @param {String} nextWebTask 		Next web task based on product post registration task
   */
  submitRegistrationPage(publicKey, keyDatetime, nextWebTask) {
    InternetBankingActions.raise(
      InternetBankingConstants.SUBMIT_REGISTRATION_PAGE,
      {
        publicKey,
        keyDatetime,
        nextWebTask,
      }
    );
  },

  /**
   * Force autosaving of the user's data to start.
   * Successful save calls automatically trigger the next autosave timer, so this
   * is only needed for the initial kick-off.
   */
  startAutosaveTimer() {
    InternetBankingActions.raise(InternetBankingConstants.START_AUTOSAVE_TIMER);
  },

  /**
   * Update offer status.
   * @param  {Event} isAltProduct is offer downgraded or not
   */
  updateOfferStatus(isAltProduct) {
    InternetBankingActions.raise(InternetBankingConstants.UPDATE_OFFER_STATUS, {
      isAltProduct,
    });
  },

  /**
   * Update username
   * @param  {username}
   */
  updateUserName(userName) {
    InternetBankingActions.raise(InternetBankingConstants.UPDATE_USERNAME, {
      userName,
    });
  },

  /**
   * Bank ID may or may not be present in the data store from app start
   * @param  {brandId}
   */
  setBrandId(brandID) {
    InternetBankingActions.raise(InternetBankingConstants.SET_BRAND_ID, {
      brandID,
    });
  },

  /**
   * Clear any bankID that has been set
   */
  resetBrandId() {
    InternetBankingActions.raise(InternetBankingConstants.RESET_BRAND_ID);
  },

  /**
   * Clear any username that has been set
   */
  resetUsername() {
    InternetBankingActions.raise(InternetBankingConstants.CLEAR_USERNAME);
  },

  /**
   * Clear any userInfo that has been set
   */
  resetUserInfo(userInfo) {
    InternetBankingActions.raise(InternetBankingConstants.CLEAR_USERINFO, {
      userInfo,
    });
  },

  /**
   * Receives Http get response body
   * @param  {object} result Http response body
   */
  receiveGetResult(result) {
    InternetBankingActions.raise(InternetBankingConstants.RECEIVE_GET_RESULT, {
      result,
    });
  },

  /**
   * Records that a task has been completed
   * @param  {string} taskId Id of the task that has just been completed
   */
  recordTaskComplete(taskId) {
    InternetBankingActions.raise(
      InternetBankingConstants.RECORD_TASK_COMPLETE,
      { taskId }
    );
  },

  getTermsAndConditions() {
    InternetBankingActions.raise(
      InternetBankingConstants.REQUEST_TERMSANDCONDITIONS_DETAILS
    );
  },

  handleTermsAndConditionsSuccess(response) {
    InternetBankingActions.raise(
      InternetBankingConstants.REQUEST_TERMSANDCONDITIONS_DETAILS_SUCCESS,
      { response }
    );
  },

  setBatchPayment(isBatch) {
    InternetBankingActions.raise(
      InternetBankingConstants.SET_BATCH_PAYMENT_MODE,
      { isBatch }
    );
  },
};

export default InternetBankingActions;
